import { CalculationScopeModel } from "../api/APIClient";

/**
 * Calculation scopes - the shared vocabulary for "which slice of the world does this act on".
 *
 * The same shape drives a scoped calculation run, a preview, and a mark-workflow action, so it lives
 * here rather than in any one of them.
 *
 * Rules the server enforces and the UI has to respect:
 *   - Multiple scopes INTERSECT. Two scopes with no overlap is a 400, not an empty result.
 *   - `QualificationProgramLevelCycle` needs both a program level and a cycle.
 *   - `includeDescendants` only means anything for `AssessmentGroup`.
 *   - A scoped run always closes over downstream assignments, so chained procedures resolve without
 *     the caller naming their dependencies.
 */

export const SCOPE_KINDS = [
    "Assignment",
    "QualificationModule",
    "AssessmentGroup",
    "TeachingGroup",
    "Student",
    "QualificationProgramLevelCycle",
] as const;

export type ScopeKind = (typeof SCOPE_KINDS)[number];

export const SCOPE_LABELS: Record<ScopeKind, string> = {
    Assignment: "Procedure assignment",
    QualificationModule: "Qualification module",
    AssessmentGroup: "Assessment group",
    TeachingGroup: "Teaching group",
    Student: "Student",
    QualificationProgramLevelCycle: "Cohort and cycle",
};

export interface CalculationScope {
    kind: ScopeKind | string;
    ids: string[];
    includeDescendants?: boolean;
    qualificationProgramLevelID?: string;
    assessmentCycleID?: string;
}

/**
 * NSwag names the id collection `iDs` (from the C# `IDs` via camelCase). ASP.NET Core matches JSON
 * property names case-insensitively, so this binds correctly - but it is why the property looks
 * misspelt. Do not "fix" it.
 */
export const toScopeModel = (scope: CalculationScope): CalculationScopeModel =>
    new CalculationScopeModel({
        kind: scope.kind,
        iDs: scope.ids,
        includeDescendants: scope.includeDescendants,
        qualificationProgramLevelID: scope.qualificationProgramLevelID,
        assessmentCycleID: scope.assessmentCycleID,
    });

export const toScopeModels = (scopes: CalculationScope[]): CalculationScopeModel[] => scopes.map(toScopeModel);

/** A single-assignment scope - what the procedure screen uses after activating an assignment. */
export const assignmentScope = (assignmentID: string): CalculationScope[] => [{ kind: "Assignment", ids: [assignmentID] }];

export const moduleScope = (qualificationModuleID: string): CalculationScope[] => [
    { kind: "QualificationModule", ids: [qualificationModuleID] },
];

/** Human-readable summary of a scope list, for confirmations and run summaries. */
export function describeScopes(scopes: CalculationScope[]): string {
    if (!scopes.length) return "nothing";
    return scopes
        .map((s) => {
            const label = SCOPE_LABELS[s.kind as ScopeKind] ?? s.kind;
            const count = s.ids.length;
            return count === 1 ? label.toLowerCase() : `${count} ${label.toLowerCase()}s`;
        })
        .join(" and ");
}
